import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  DocumentationCategory,
  DocumentationCategoryFilterInputObject,
  EdmDocumentationCategoriesQueryVariables,
  FilterGroupingEnum,
  useEdmDocumentationCategoriesQuery,
} from 'graphql-common';
import {
  GetUrlParams,
  ORDERING_DIRECTION_ASC,
  OrderingParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import LinkComponent, { LinkSizes, LinkTypes } from '@lib/components/Link/Link';
import Chip from '@lib/components/Chip/Chip';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import styles from './DocumentationCategoryChipList.module.scss';

function DocumentationCategoryChipList({ ids }: { ids: string[] }) {
  const { t } = useTranslation();
  const [paginationParams, setPaginationParams] = useState<
    PaginationParams & OrderingParams
  >({
    ...PAGINATION_PARAMS,
    [GetUrlParams.OrderingField]: 'name',
    [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_ASC,
  });
  const [allItems, setAllItems] = useState<DocumentationCategory[]>([]);

  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    paginationParams,
    GetUrlParams.OrderingDirection,
  );

  const queryVariables: EdmDocumentationCategoriesQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
  };

  const filters: DocumentationCategoryFilterInputObject[] = [];
  if (ids?.length) {
    filters.push({
      id: {
        grouping: FilterGroupingEnum.Or,
        predicate: {
          in: ids,
        },
      },
    });
  }
  if (filters.length) {
    queryVariables.filters = filters;
  }
  if (orderingField && orderingDirection) {
    queryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }

  const query = useEdmDocumentationCategoriesQuery({
    fetchPolicy: 'no-cache',
    variables: queryVariables,
  });

  const { loading, data, previousData, error } = query;
  const queryData = data || previousData;
  const firstLoading = loading && previousData === undefined;
  const newItems = _get(
    queryData,
    'data.collection',
    [],
  ) as DocumentationCategory[];
  const metadata = _get(queryData, 'data.metadata');
  const totalPages = _get(metadata, 'totalPages', 1);
  const currentPage = _get(metadata, 'currentPage', 1);

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      setPaginationParams((prev) => ({
        ...prev,
        [GetUrlParams.Page]: `${currentPage + 1}`,
      }));
    }
  };

  useEffect(() => {
    if (newItems.length) {
      setAllItems((prevItems) => [...prevItems, ...newItems]);
    }
  }, [newItems]);

  if (error || firstLoading) return null;

  return (
    <div>
      <div className={styles.chipsList}>
        {allItems?.length
          ? allItems.map((item) => (
              <Chip
                label={item.name}
                key={item.id}
                type={colorToChipType(item.color)}
                leftIcon="circle"
              />
            ))
          : null}
      </div>
      {!!allItems?.length && currentPage < totalPages && (
        <LinkComponent
          size={LinkSizes.small}
          type={LinkTypes.secondaryGray}
          onClick={handleLoadMore}
        >
          {t('show-more')}
        </LinkComponent>
      )}
    </div>
  );
}

export default memo(DocumentationCategoryChipList);
